import React, { useEffect, useState, useMemo, useRef } from 'react';
import { LogoUNEA } from '../../components/common/Logo';
import { useFrontDeskAuth } from '../../context/FrontDeskAuthContext';
import { useTenant } from '../../context/TenantContext';
import FrontDeskLogin from './FrontDeskLogin';

const NAVY      = '#1A3566';
const ACCENT    = '#0085CA';
const ACCENT_BG = '#e8f0fe';
const BORDER    = '#C7D3EC';


/* ─────────────────────────────────────── estilos globales inline ─── */
const page = {
  minHeight: '100vh',
  background: '#F5F5F5',
  fontFamily: "'Mulish', 'Inter', sans-serif",
  color: '#111827',
};

const header = {
  height: 56,
  background: '#fff',
  borderBottom: '2px solid #0288D1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  position: 'sticky',
  top: 0,
  zIndex: 100,
  flexShrink: 0,
};


const searchWrap = {
  maxWidth: 560,
  margin: '24px auto 0',
  position: 'relative',
};

const searchInput = {
  width: '100%',
  padding: '12px 44px 12px 16px',
  borderRadius: 10,
  border: 'none',
  fontSize: 14,
  outline: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,.15)',
  boxSizing: 'border-box',
};

const sectionCard = {
  background: '#fff',
  borderRadius: 12,
  border: `1px solid #E5E7EB`,
  overflow: 'hidden',
};

const sectionHeader = {
  padding: '0 16px',
  height: 55,
  display: 'flex',
  alignItems: 'center',
  background: ACCENT_BG,
  borderBottom: '1px solid #E5E7EB',
  fontSize: 16,
  fontWeight: 700,
  color: ACCENT,
  letterSpacing: 0.2,
};

const contactRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 16px',
  height: 52,
  boxSizing: 'border-box',
  borderBottom: '1px solid #F3F4F6',
  fontSize: 13,
};

const extBadge = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 13,
  fontWeight: 700,
  color: '#0085ca',
  background: ACCENT_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 16,
  padding: '2px 8px',
  minWidth: 55,
  width: 55,
  textAlign: 'center',
  flexShrink: 0,
};

/* ──────────────────────────────────────────────── componentes ─────── */

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', pointerEvents:'none' }}>
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}

function PhoneIcon({ size = 14, color = ACCENT }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.91a16 16 0 006.1 6.1l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    </svg>
  );
}

const normalize = str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function SectionGrid({ sections, query }) {
  const filtered = useMemo(() => {
    if (!query) return sections;
    const q = normalize(query);
    return sections
      .map(s => {
        const sectionMatch = normalize(s.name).includes(q);
        // Si el nombre de la sección coincide, mostrar todos sus contactos
        if (sectionMatch) return s;
        // Si no, filtrar solo los contactos que coincidan
        const contacts = s.contacts.filter(c =>
          normalize(c.name).includes(q) ||
          normalize(c.role || '').includes(q) ||
          c.extension.includes(q)
        );
        return { ...s, contacts };
      })
      .filter(s => s.contacts.length > 0);
  }, [sections, query]);

  if (!filtered.length && !query) return null;
  if (!filtered.length) return (
    <div style={{ textAlign:'center', padding:'48px 0', color:'#9CA3AF', fontSize:14 }}>
      No se encontraron resultados para "{query}"
    </div>
  );

  return (
    <div className="ext-sections-grid">
      {filtered.map(section => (
        <div key={section.id} style={sectionCard}>
          <div style={sectionHeader}>{section.name}</div>
          <div>
            {section.contacts.map((c, i) => (
              <div key={c.id} style={{ ...contactRow }}>
                <div style={{ minWidth: 0, marginRight: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {c.name}
                  </div>
                  {c.role && (
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>{c.role}</div>
                  )}
                </div>
                <span style={extBadge}>{c.extension}</span>
              </div>
            ))}
            {section.contacts.length === 0 && (
              <div style={{ padding:'12px 16px', fontSize:12, color:'#9CA3AF' }}>Sin contactos</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PdiSection({ pdi, query }) {
  const filtered = useMemo(() => {
    // Filtrar entradas sin nombre real (solo número o guión)
    const valid = pdi.filter(p => p.name && !/^[\d\s\-]+$/.test(p.name.trim()));
    if (!query) return valid;
    const q = normalize(query);
    return valid.filter(p => normalize(p.name).includes(q) || p.extension.includes(q));
  }, [pdi, query]);

  const cols = useMemo(() => {
    const N = 4;
    const size = Math.ceil(filtered.length / N);
    return Array.from({ length: N }, (_, i) => filtered.slice(i * size, (i + 1) * size));
  }, [filtered]);

  if (!filtered.length && query) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <div style={{ height:1, flex:1, background:'#E5E7EB' }}/>
        <span style={{ fontSize:16, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:1 }}>
          Directorio completo
        </span>
        <div style={{ height:1, flex:1, background:'#E5E7EB' }}/>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'24px 0', color:'#9CA3AF', fontSize:14 }}>
          No hay datos Todas las extensiones disponibles en este momento
        </div>
      ) : (
        <div style={sectionCard}>
          <div style={{ ...sectionHeader, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span>Todas las extensiones</span>
            <span style={{ fontSize:11, fontWeight:400, color:'#6b7280' }}>{filtered.length} extensiones</span>
          </div>
          <div className="ext-pdi-grid">
            {cols.map((col, ci) => (
              <div key={ci} style={{ borderRight: '1px solid #F3F4F6' }}>
                {col.map((p, i) => (
                  <div key={i} style={{ ...contactRow, flexWrap:'nowrap' }}>
                    <span style={{ fontSize:14, fontWeight:500, color:'#111827', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginRight:8 }}>
                      {p.name}
                    </span>
                    <span style={extBadge}>{p.extension}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ padding:'10px 16px', background:'#FAFAFA', borderTop:'1px solid #F3F4F6', fontSize:11, color:'#9CA3AF' }}>
            Los nombres se muestran tal como figuran en el sistema de telefonía (pueden aparecer sin tildes).
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────── página principal ── */

export default function ExtensionsPage() {
  const { canAccess, fdLoading, fdUser, fdLogout } = useFrontDeskAuth();
  const { modules } = useTenant() || {};
  const asteriskMod = Array.isArray(modules) ? [...modules].reverse().find(m => m.module === 'asterisk') : undefined;
  const hasAsterisk = !Array.isArray(modules) || !asteriskMod || Number(asteriskMod.active) !== 0;
  const [data,    setData]    = useState({ sections: [], pdi: [] });
  const [loading, setLoading] = useState(true);
  const [query,   setQuery]   = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    document.title = 'Directorio de Extensiones — UNEATLANTICO';
    return () => { document.title = 'HelpDesk TI — UNEATLANTICO'; };
  }, []);

  useEffect(() => {
    if (!canAccess) return;
    fetch('/api/extensions')
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [canAccess]);

  if (fdLoading) return null;
  if (!canAccess) return <FrontDeskLogin error={null} />;

  const showPdi = hasAsterisk && (!query || data.pdi.some(p =>
    normalize(p.name).includes(normalize(query)) ||
    p.extension.includes(query)
  ));

  return (
    <div style={page}>
      <style>{`
        .ext-sections-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
        .ext-pdi-grid      { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; }
        .ext-appbar-title  { font-size: 16px; font-weight: 400; color: #111827; }
        .ext-funiber-link  { display: flex; align-items: center; gap: 6px; color: #6B7280; font-size: 13px; text-decoration: none; }
        @media (max-width: 768px) {
          .ext-sections-grid { grid-template-columns: 1fr; }
          .ext-pdi-grid      { grid-template-columns: repeat(2, 1fr); }
          .ext-appbar-title  { font-size: 14px; }
          .ext-funiber-link  { font-size: 12px; }
        }
        @media (max-width: 480px) {
          .ext-pdi-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      {/* AppBar — mismo diseño que BackDeskLayout */}
      <header style={header}>
        <div style={{ display:'flex', alignItems:'center', gap:0 }}>
          <LogoUNEA height={32} style={{ display:'block' }}/>
          <div style={{ width:1, height:28, background:'#E5E7EB', margin:'0 16px' }}/>
          <span className="ext-appbar-title">Directorio de Extensiones</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <a href="https://extensiones.funiber.org" target="_blank" rel="noopener noreferrer"
            className="ext-funiber-link"
            style={{ textDecoration:'none' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            Extensiones Funiber
          </a>
          {fdUser && (
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              {fdUser.picture
                ? <img src={fdUser.picture} alt="" style={{width:28,height:28,borderRadius:'50%',border:'2px solid #C5D8E8'}}/>
                : <div style={{width:28,height:28,borderRadius:'50%',background:'#0085CA',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700}}>{fdUser.name?.[0]?.toUpperCase()}</div>
              }
              <button onClick={fdLogout}
                style={{fontSize:12,color:'#9CA3AF',background:'none',border:'none',cursor:'pointer'}}>
                Salir
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Contenido */}
      <div style={{ maxWidth: 1450, margin: '0 auto', padding: '24px 20px 48px' }}>
        {/* Buscador */}
        <div style={{ position:'relative', marginBottom:24 }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por nombre, cargo o extensión..."
            style={{ ...searchInput, boxShadow: 'none', border: '1px solid #E5E7EB' }}
            autoFocus
          />
          {query
            ? <button onClick={() => setQuery('')} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', fontSize:16, lineHeight:1 }}>×</button>
            : <SearchIcon/>
          }
        </div>
        {loading ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'#9CA3AF' }}>
            <div style={{ width:32, height:32, border:`3px solid ${BORDER}`, borderTopColor:ACCENT, borderRadius:'50%', animation:'spin 1s linear infinite', margin:'0 auto 12px' }}/>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Cargando directorio…
          </div>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <div style={{ height:1, flex:1, background:'#E5E7EB' }}/>
              <span style={{ fontSize:16, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:1 }}>
                Directorio por departamento
              </span>
              <div style={{ height:1, flex:1, background:'#E5E7EB' }}/>
            </div>
            <SectionGrid sections={data.sections} query={query}/>
            {showPdi && <PdiSection pdi={data.pdi} query={query}/>}

          </>
        )}
      </div>
    </div>
  );
}
